import React, { useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Avatar,
  Box,
  Typography,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  Stack,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { validateRut, deconstructRut } from "@fdograph/rut-utilities";

import {
  useUpdateEmpleadoMutation,
  useUploadFotoEmpleadoMutation,
  useDeleteFotoEmpleadoMutation,
  useAddEmpleadoMutation,
  useUpdateCambioContrasenaMutation,
} from "src/services/empleadosApi";
import { ajustesApi } from "src/services/ajustesApi";

import WebcamCaptureDialog from "src/components/comunes/WebcamCaptureDialog";
import ImageCropDialog from "src/components/comunes/ImageCropDialog";
import AICorrectButton from "src/components/propuesta/AICorrectButton";
import {
  validateUploadFile,
  FOTO_PERFIL_RULES,
} from "src/utils/uploadValidations";

// Feature detection: webcam disponible solo si el navegador soporta getUserMedia
const tieneWebcam =
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

const SeccionFoto = ({
  rut,
  fotoUrl,
  nombre,
  refSucursal,
  onFotoChange,
  enqueueSnackbar,
}) => {
  const fileInputRef = useRef(null);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [imagenSrcParaCrop, setImagenSrcParaCrop] = useState(null);

  const [uploadFoto, { isLoading: uploadLoading }] =
    useUploadFotoEmpleadoMutation();
  const [deleteFoto, { isLoading: deleteLoading }] =
    useDeleteFotoEmpleadoMutation();

  const procesarBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    setImagenSrcParaCrop(url);
    setCropOpen(true);
  };

  const handleSubirFoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // Permite re-seleccionar el mismo archivo
    if (!file) return;

    const { valid, error } = validateUploadFile(file, FOTO_PERFIL_RULES);
    if (!valid) {
      enqueueSnackbar(error, { variant: "error" });
      return;
    }
    procesarBlob(file);
  };

  const handleWebcamCapture = (blob) => {
    procesarBlob(blob);
  };

  const handleCropAplicar = async (blob) => {
    try {
      const result = await uploadFoto({
        rut,
        file: blob,
        refSucursal,
      }).unwrap();
      onFotoChange(result.foto_url);
      enqueueSnackbar("Foto actualizada", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "No se pudo actualizar la foto", {
        variant: "error",
      });
    } finally {
      if (imagenSrcParaCrop) URL.revokeObjectURL(imagenSrcParaCrop);
      setImagenSrcParaCrop(null);
    }
  };

  const handleEliminar = async () => {
    try {
      await deleteFoto({ rut, refSucursal }).unwrap();
      onFotoChange(null);
      enqueueSnackbar("Foto eliminada", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.data?.message || "No se pudo eliminar la foto", {
        variant: "error",
      });
    }
  };

  const iniciales = nombre ? nombre.charAt(0).toUpperCase() : "?";
  const loading = uploadLoading || deleteLoading;

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        Foto
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
        <Avatar
          src={fotoUrl}
          sx={{
            width: 88,
            height: 88,
            bgcolor: "#d57a23",
            fontSize: 32,
            fontWeight: 600,
          }}
        >
          {iniciales}
        </Avatar>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            hidden
            onChange={handleSubirFoto}
          />
          <Button
            variant="outlined"
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
          >
            Subir foto
          </Button>
          {tieneWebcam && (
            <Button
              variant="outlined"
              disabled={loading}
              onClick={() => setWebcamOpen(true)}
            >
              Tomar foto con cámara
            </Button>
          )}
          {fotoUrl && (
            <Button
              variant="outlined"
              color="error"
              disabled={loading}
              onClick={handleEliminar}
            >
              Eliminar
            </Button>
          )}
        </Stack>
      </Box>

      <WebcamCaptureDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onCapture={handleWebcamCapture}
      />
      <ImageCropDialog
        open={cropOpen}
        onClose={() => {
          if (imagenSrcParaCrop) URL.revokeObjectURL(imagenSrcParaCrop);
          setImagenSrcParaCrop(null);
          setCropOpen(false);
        }}
        imageSrc={imagenSrcParaCrop}
        onCrop={handleCropAplicar}
      />
    </Box>
  );
};

SeccionFoto.propTypes = {
  rut: PropTypes.string.isRequired,
  fotoUrl: PropTypes.string,
  nombre: PropTypes.string,
  refSucursal: PropTypes.string.isRequired,
  onFotoChange: PropTypes.func.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
};

const SeccionCambiarContrasena = ({ enqueueSnackbar }) => {
  const [expandido, setExpandido] = useState(false);
  const [showPass1, setShowPass1] = useState(false);
  const [showPass2, setShowPass2] = useState(false);
  const [updateContrasena, { isLoading }] = useUpdateCambioContrasenaMutation();

  const PasswordSchema = Yup.object().shape({
    nuevaContrasena: Yup.string()
      .min(8, "Mínimo 8 caracteres")
      .required("Contraseña requerida"),
    confirmarNuevaContrasena: Yup.string()
      .oneOf([Yup.ref("nuevaContrasena")], "Las contraseñas no coinciden")
      .required("Confirmación requerida"),
  });

  const formikPass = useFormik({
    initialValues: { nuevaContrasena: "", confirmarNuevaContrasena: "" },
    validationSchema: PasswordSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await updateContrasena({
          nuevaContrasena: values.nuevaContrasena,
        }).unwrap();
        enqueueSnackbar("Contraseña actualizada", { variant: "success" });
        resetForm();
        setExpandido(false);
      } catch (err) {
        enqueueSnackbar(
          err?.data?.message || "No se pudo actualizar la contraseña",
          { variant: "error" },
        );
      }
    },
  });

  return (
    <Box>
      <Box
        onClick={() => setExpandido((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          py: 1,
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: "text.secondary", fontWeight: 600 }}
        >
          Cambiar contraseña
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {expandido ? "▲" : "▼"}
        </Typography>
      </Box>
      <Collapse in={expandido}>
        <FormikProvider value={formikPass}>
          <Box>
            <TextField
              fullWidth
              margin="dense"
              label="Nueva contraseña"
              type={showPass1 ? "text" : "password"}
              {...formikPass.getFieldProps("nuevaContrasena")}
              error={Boolean(
                formikPass.touched.nuevaContrasena &&
                formikPass.errors.nuevaContrasena,
              )}
              helperText={
                formikPass.touched.nuevaContrasena &&
                formikPass.errors.nuevaContrasena
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass1((v) => !v)}
                      edge="end"
                    >
                      <Icon icon={showPass1 ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Confirmar nueva contraseña"
              type={showPass2 ? "text" : "password"}
              {...formikPass.getFieldProps("confirmarNuevaContrasena")}
              error={Boolean(
                formikPass.touched.confirmarNuevaContrasena &&
                formikPass.errors.confirmarNuevaContrasena,
              )}
              helperText={
                formikPass.touched.confirmarNuevaContrasena &&
                formikPass.errors.confirmarNuevaContrasena
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass2((v) => !v)}
                      edge="end"
                    >
                      <Icon icon={showPass2 ? eyeFill : eyeOffFill} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <LoadingButton
                type="button"
                variant="contained"
                loading={isLoading}
                onClick={formikPass.handleSubmit}
              >
                Guardar nueva contraseña
              </LoadingButton>
            </Box>
          </Box>
        </FormikProvider>
      </Collapse>
    </Box>
  );
};

SeccionCambiarContrasena.propTypes = {
  enqueueSnackbar: PropTypes.func.isRequired,
};

const EmpleadoFormDialog = ({ open, onClose, mode, defaultValues = {} }) => {
  const { enqueueSnackbar } = useSnackbar();
  const auth = useSelector((state) => state.auth.user);
  const refSucursal = auth?.sucursal?.refSucursal;
  const rolActivo = auth?.rol;
  const authRut = auth?.empleado?.rut;

  // Determinar variant
  const esAdmin = ["SUPER_ADMIN", "ADMIN"].includes(rolActivo);
  const esSelf = mode === "edit" && authRut === defaultValues?.rut;
  const variant = esAdmin ? "admin" : esSelf ? "self-edit" : "admin";

  const camposAdminDisabled = variant === "self-edit";

  const [addEmpleado, { isLoading: creando }] = useAddEmpleadoMutation();
  const [updateEmpleado, { isLoading: actualizando }] =
    useUpdateEmpleadoMutation();
  const { data: roles = [] } = ajustesApi.useGetRolesQuery();

  const FormSchema = Yup.object().shape({
    nombre: Yup.string().trim().required("Nombre requerido"),
    apellidos: Yup.string().trim().required("Apellidos requeridos"),
    rut: Yup.string().test("rut", "RUT no válido", (v) => !v || validateRut(v)),
    telefono: Yup.string().test(
      "tel",
      "El teléfono debe tener 9 dígitos",
      (v) => !v || v.length === 9,
    ),
    correo: Yup.string().email("Correo no válido").required("Correo requerido"),
    estado: Yup.string().oneOf(["ACTIVO", "INACTIVO"]),
    cargo: Yup.string().trim().max(100, "Máximo 100 caracteres").nullable(),
    bio: Yup.string().trim().max(500, "Máximo 500 caracteres").nullable(),
    especialidades: Yup.string()
      .trim()
      .max(300, "Máximo 300 caracteres")
      .nullable(),
    anios_experiencia: Yup.number()
      .integer("Debe ser entero")
      .min(0)
      .max(70, "Máximo 70 años")
      .nullable(),
  });

  const initialValues = useMemo(
    () => ({
      rut: defaultValues.rut || "",
      nombre: defaultValues.nombre || "",
      apellidos: defaultValues.apellidos || "",
      telefono: defaultValues.telefono || "",
      correo: defaultValues.correo || "",
      estado: defaultValues.estado || "ACTIVO",
      fechaIngreso: defaultValues.fechaIngreso || null,
      refRol: defaultValues.refRol || "",
      cargo: defaultValues.cargo || "",
      bio: defaultValues.bio || "",
      especialidades: defaultValues.especialidades || "",
      anios_experiencia: defaultValues.anios_experiencia ?? "",
      foto_url: defaultValues.foto_url || null,
    }),
    [defaultValues],
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: FormSchema,
    onSubmit: async (values) => {
      try {
        const rutLimpio = String(values.rut)
          .replace(/\./g, "")
          .replace(/-/g, "")
          .toUpperCase();

        const payload = {
          nombre: values.nombre.trim().toUpperCase(),
          apellidos: values.apellidos.trim().toUpperCase(),
          telefono: String(values.telefono).trim().toUpperCase(),
          correo: values.correo.trim(),
          cargo: values.cargo.trim() || null,
          bio: values.bio.trim() || null,
          especialidades: values.especialidades.trim() || null,
          anios_experiencia:
            values.anios_experiencia === ""
              ? null
              : Number(values.anios_experiencia),
          refSucursal,
        };

        if (variant === "admin") {
          payload.estado = values.estado;
          payload.fechaIngreso = values.fechaIngreso;
          // refRol solo se envía al crear (asigna relación inicial empleado-sucursal-rol).
          // En edición, el cambio de rol se hace desde el modal "Sucursales" del listado.
          if (mode === "create") payload.refRol = values.refRol;
        }

        if (mode === "create") {
          await addEmpleado({
            ...payload,
            rut: rutLimpio,
            contrasena:
              deconstructRut(rutLimpio).digits +
              deconstructRut(rutLimpio).verifier,
          }).unwrap();
          enqueueSnackbar("Empleado creado", { variant: "success" });
        } else {
          await updateEmpleado({ rut: defaultValues.rut, ...payload }).unwrap();
          enqueueSnackbar("Empleado actualizado", { variant: "success" });
        }
        onClose();
      } catch (err) {
        enqueueSnackbar(err?.data?.message || "No se pudo guardar", {
          variant: "error",
        });
      }
    },
  });

  const titulo =
    mode === "create"
      ? "Agregar empleado"
      : variant === "self-edit"
        ? "Mi perfil"
        : `Editar empleado · ${defaultValues.nombre} ${defaultValues.apellidos}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{titulo}</DialogTitle>
      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
            <Stack spacing={3}>
              {/* === Foto === (solo en edit, no en create — la foto se sube post-creación) */}
              {mode === "edit" && (
                <>
                  <SeccionFoto
                    rut={defaultValues.rut}
                    fotoUrl={formik.values.foto_url}
                    nombre={formik.values.nombre}
                    refSucursal={refSucursal}
                    onFotoChange={(url) =>
                      formik.setFieldValue("foto_url", url)
                    }
                    enqueueSnackbar={enqueueSnackbar}
                  />
                  <Divider />
                </>
              )}

              {/* === Datos personales === */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Datos personales
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre"
                      {...formik.getFieldProps("nombre")}
                      error={Boolean(
                        formik.touched.nombre && formik.errors.nombre,
                      )}
                      helperText={formik.touched.nombre && formik.errors.nombre}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Apellidos"
                      {...formik.getFieldProps("apellidos")}
                      error={Boolean(
                        formik.touched.apellidos && formik.errors.apellidos,
                      )}
                      helperText={
                        formik.touched.apellidos && formik.errors.apellidos
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="RUT"
                      {...formik.getFieldProps("rut")}
                      disabled={mode === "edit"}
                      error={Boolean(formik.touched.rut && formik.errors.rut)}
                      helperText={formik.touched.rut && formik.errors.rut}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teléfono"
                      type="number"
                      {...formik.getFieldProps("telefono")}
                      error={Boolean(
                        formik.touched.telefono && formik.errors.telefono,
                      )}
                      helperText={
                        formik.touched.telefono && formik.errors.telefono
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Correo"
                      type="email"
                      {...formik.getFieldProps("correo")}
                      disabled={camposAdminDisabled}
                      error={Boolean(
                        formik.touched.correo && formik.errors.correo,
                      )}
                      helperText={formik.touched.correo && formik.errors.correo}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Divider />

              {/* === Presentación profesional === */}
              <Box>
                <Typography
                  variant="overline"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  Presentación profesional
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cargo"
                      placeholder="Ej: Socio Director, Gerente de Proyectos"
                      {...formik.getFieldProps("cargo")}
                      error={Boolean(
                        formik.touched.cargo && formik.errors.cargo,
                      )}
                      helperText={formik.touched.cargo && formik.errors.cargo}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Bio profesional"
                      {...formik.getFieldProps("bio")}
                      error={Boolean(formik.touched.bio && formik.errors.bio)}
                      helperText={formik.touched.bio && formik.errors.bio}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 0.5,
                      }}
                    >
                      <AICorrectButton
                        text={formik.values.bio}
                        context="Biografía profesional breve de un empleado de un estudio contable/tributario chileno. Destacar experiencia técnica y especialidades. Evitar promocionar al estudio en sí."
                        onCorrected={(t) => formik.setFieldValue("bio", t)}
                        disabled={!formik.values.bio}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Especialidades"
                      placeholder="Ej: Precios de transferencia, IFRS, Planificación"
                      {...formik.getFieldProps("especialidades")}
                      error={Boolean(
                        formik.touched.especialidades &&
                        formik.errors.especialidades,
                      )}
                      helperText={
                        formik.touched.especialidades &&
                        formik.errors.especialidades
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Años de experiencia"
                      {...formik.getFieldProps("anios_experiencia")}
                      error={Boolean(
                        formik.touched.anios_experiencia &&
                        formik.errors.anios_experiencia,
                      )}
                      helperText={
                        formik.touched.anios_experiencia &&
                        formik.errors.anios_experiencia
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* === Administración del sistema === (solo admin) */}
              {variant === "admin" && (
                <>
                  <Divider />
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      Administración del sistema
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                      {mode === "create" && (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Rol</InputLabel>
                            <Select
                              label="Rol"
                              value={formik.values.refRol}
                              onChange={(e) =>
                                formik.setFieldValue("refRol", e.target.value)
                              }
                            >
                              {roles
                                .filter((r) => r.nombre !== "CLIENTE")
                                .map((r) => (
                                  <MenuItem key={r.nombre} value={r.nombre}>
                                    {r.nombre}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={mode === "create" ? 6 : 12}>
                        <FormControl fullWidth>
                          <InputLabel>Estado</InputLabel>
                          <Select
                            label="Estado"
                            value={formik.values.estado}
                            onChange={(e) =>
                              formik.setFieldValue("estado", e.target.value)
                            }
                          >
                            <MenuItem value="ACTIVO">Activo</MenuItem>
                            <MenuItem value="INACTIVO">Inactivo</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {mode === "edit" && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Para cambiar el rol y las sucursales asignadas, usar
                            el botón "Sucursales" del listado de empleados.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </>
              )}

              {/* === Cambiar contraseña === (solo en edit, self-edit) */}
              {mode === "edit" && esSelf && (
                <>
                  <Divider />
                  <SeccionCambiarContrasena enqueueSnackbar={enqueueSnackbar} />
                </>
              )}
            </Stack>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          loading={creando || actualizando || formik.isSubmitting}
          disabled={formik.isSubmitting}
          onClick={formik.handleSubmit}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EmpleadoFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  defaultValues: PropTypes.object,
};

export default EmpleadoFormDialog;
